import { Coins,
   PlusCircle, 
   Cpu, 
   Gauge,
   AlertTriangle, 
   ArrowRight,
   Download,
   Loader2,
   Search,
   X, 
   ArrowUpDown,
   Trash2} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../Components/StakingRisk/ui/select";
import NewProductModal from "../../Components/StakingRisk/dashboard/NewProductModal";
import { Tabs, TabsList, TabsTrigger } from "../../Components/StakingRisk/ui/tabs";
import { GetStakingRiskProductsThunk } from "../../Store/Thunks/StakingRiskThunk";
import ProductList from "../../Components/StakingRisk/dashboard/ProductList";
import StatsCard from "../../Components/StakingRisk/dashboard/StatsCard";
import { Button } from  "../../Components/StakingRisk/ui/button";
import { Input } from "../../Components/StakingRisk/ui/input";

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const HIGH_RISK_SAFETY_INDEX = 30;

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sortBy, setSortBy] = useState("created_date_desc");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  const products = useSelector((state) => state.StakingRiskReducer.stakingRiskList);
  const productId = useSelector((state) => state.StakingRiskReducer.createdStakingRiskId);
  const created = useSelector((state) => state.StakingRiskReducer.created);
  
    const handleTabChange = (value) => {
    setActiveTab(value);  
     };
    useEffect(() => {
      const delayDebounce = setTimeout(() => {
        setDebouncedSearch(searchQuery);
      }, 500); // 500ms debounce

      return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    useEffect(() => {
      const param = {};

      if (activeTab !== "all") {
        param.scoreLevel = activeTab;
      }

      if (debouncedSearch.trim()) {
        param.search = debouncedSearch.trim();
      }

      dispatch(GetStakingRiskProductsThunk(param));
    }, [debouncedSearch, activeTab]);

      useEffect(() => {
      if (created) {
        navigate(`/stakingrisk/product-detail/${productId}`);
      }
    }, [created]);

  const categoriesConfig = [
    { key: 'asset', name: 'Asset', params: ['maturity', 'projectRisk', 'historicalRisk', 'concentration', 'regulatoryCompliance', 'amlCompliance', 'foundersTeam', 'reputation', 'lawsuits'] },
    { key: 'blockchain', name: 'Blockchain', params: ['maturity', 'projectRisk', 'historicalRisk', 'concentration', 'regulatoryCompliance', 'amlCompliance', 'foundersTeam', 'reputation', 'lawsuits'] },
    { key: 'stakingPlatform', name: 'Staking Platform', params: ['maturity', 'security', 'historicalSecurityRisk', 'historicalSlashingRisk', 'decentralization', 'regulatoryCompliance', 'amlCompliance', 'foundersTeam', 'reputation', 'legalStanding', 'uptime'] },
    { key: 'validator', name: 'Validator', params: ['maturity', 'security', 'historicalSecurityRisk', 'historicalSlashingRisk', 'decentralization', 'regulatoryCompliance', 'amlCompliance', 'foundersTeam', 'reputation', 'legalStanding', 'uptime'] },
    { key: 'stakingMechanism', name: 'Staking Mechanism', params: ['stakingMechanismRisk'] },
    { key: 'slashing', name: 'Slashing', params: ['slashingRisk'] },
    { key: 'liquidity', name: 'Liquidity', params: ['liquidityRisk'] },
    { key: 'stakedValue', name: 'Staked Value', params: ['stakedValueRisk', 'stakedAmount'] },
    { key: 'tvl', name: 'TVL', params: ['tvlRisk'] },
    { key: 'stakingByCompetitors', name: 'Staking by Competitors', params: ['competitorsRisk'] },
    { key: 'stakingProcedure', name: 'Staking Procedure', params: ['procedureRisk'] },
  ];

  const handleExportToExcel = async () => {
    setIsExporting(true);
      try {
        let csvContent = "";        
       // Build headers
        const headers = ["Product Name", "Created Date"];
        categoriesConfig.forEach(cat => {
          cat.params.forEach(param => {
            headers.push(`${cat.name}_${param}_Value`);
            headers.push(`${cat.name}_${param}_Comment`);
          });
          headers.push(`${cat.name}_TotalScore`);
          headers.push(`${cat.name}_RiskScore`);
          headers.push(`${cat.name}_WeightedScore`);
        });
        csvContent += headers.join(",") + "\r\n";

        // Build rows
        products.forEach(product => {
          const row = [];
          row.push(`"${product.name}"`);
          row.push(`"${product.createdAt}"`);

          categoriesConfig.forEach(cat => {
            const categoryData = product.stakingRiskClassifications.find(c => c.key === cat.key);

            cat.params.forEach(param => {
              const assessment = categoryData?.stakingRiskAssessments.find(a => a.key === param);
              row.push(`"${assessment?.scoreLevel || ''}"`);
              row.push(`"${assessment?.comment || ''}"`);
            });

            if (categoryData) {
              const riskScore = categoryData.riskScoreType === "manual"
                ? categoryData.manualRiskScore
                : categoryData.automaticRiskScore;

              row.push(`"${categoryData.totalScore || '0'}"`);
              row.push(`"${riskScore || '0'}"`);
              row.push(`"${categoryData.weightedScore || '0'}"`);
            } else {
              // fallback if category not found
              row.push('0','0','0');
            }
      });

      csvContent += row.join(",") + "\r\n";
    });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "staking_products_export.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      // Add user-facing error message
    } finally {
      setIsExporting(false);
    }
  };

   const averageSafetyIndex = useMemo(() => {
    if (products.length === 0) return "N/A";
    let totalSafetyIndex = 0;
    let scoredProductsCount = 0;
    products.forEach(product => {
      const weightedScore = parseFloat(product.weightedScore); 
    const safetyIndex =100-weightedScore;
    if (safetyIndex !== -1) {
        totalSafetyIndex += safetyIndex;
        scoredProductsCount++;
      }
      console.log(product.name ,"-", product.weightedScore,"dashboard")
    });
    
     return scoredProductsCount > 0 ? (totalSafetyIndex / scoredProductsCount).toFixed(1) + "%" : "N/A";
  }, [products]);
     
    const highRiskCount = useMemo(() => {
      const filtered = products.filter(p => {
        const safetyIndex = parseFloat(p.safetyIndex);

        const isHighRisk = !isNaN(safetyIndex) && safetyIndex < HIGH_RISK_SAFETY_INDEX;
        return isHighRisk;
      });

        return filtered.length;
      }, [products]);

      const assessmentCompletion = useMemo(() => {
      if (products.length === 0) return "0%";

      // Each product has classifications instead of a fixed categoriesConfig length
      const totalPossiblePoints = products.reduce(
        (sum, product) => sum + (product.stakingRiskClassifications?.length || 0),
        0
      );

        let completedPoints = 0;
        products.forEach(product => {
          product.stakingRiskClassifications?.forEach(classification => {
            const totalScore = parseFloat(classification.totalScore) || 0;
            const riskScore =
              parseFloat(classification.manualRiskScore) ||
              parseFloat(classification.automaticRiskScore) ||
              0;

            if (totalScore > 0 && riskScore > 0) {
              completedPoints++;
            }
          });
        });

        const percentage = totalPossiblePoints > 0
          ? Math.round((completedPoints / totalPossiblePoints) * 100)
          : 0;

        return `${percentage}%`;
      }, [products]);

      const clearSearch = () => {
        setSearchQuery("");
      };
      const getSortLabel = (sortValue) => {
      const labels = {
        "name_asc": "Name (A-Z)",
        "name_desc": "Name (Z-A)",
        "created_date_asc": "Oldest First",
        "created_date_desc": "Newest First",
        "safety_index_asc": "Safety Index (Low-High)",
        "safety_index_desc": "Safety Index (High-Low)"
      };
      return labels[sortValue] || "Newest First";
    };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Staking Products</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage and assess your staking products
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
             <Link             
              to={"/stakingRisk/trashbox"}
              >
            <Button variant="outline" className="w-full sm:w-auto">
              <Trash2 className="mr-2 h-4 w-4" />
              Trash Box
            </Button>
          </Link>
          <Button 
            onClick={handleExportToExcel}
            variant="outline"
            disabled={isExporting || products.length === 0}
            className="w-full sm:w-auto"
          >
            {isExporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export to Excel
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create New Product
          </Button>     
         </div>
      </div>      
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatsCard 
          title="Total Products"
          value={products.length}
          icon={Coins}
          bgColor="bg-blue-600"
        />
        <StatsCard 
          title="Average Safety Index"
           value={averageSafetyIndex}
          icon={Gauge}
          bgColor="bg-purple-600"
        />      
         <StatsCard 
          title="High Risk Products"
          value={highRiskCount}
          icon={AlertTriangle}
          trend={highRiskCount > 0 ? "up" : null}
          trendValue={highRiskCount > 0 ? `${highRiskCount} product(s)` : null}
          bgColor="bg-red-600"
        />
         <StatsCard
          title="Assessment Completion"
          value={assessmentCompletion}
          icon={Cpu}
          bgColor="bg-purple-600"
        />
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <Tabs defaultValue="all" value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="all">All Products</TabsTrigger>
            <TabsTrigger value="High Risk" className="data-[state=active]:text-red-600 data-[state=active]:border-red-600">High Risk</TabsTrigger>
            <TabsTrigger value="Medium Risk" className="data-[state=active]:text-yellow-600 data-[state=active]:border-yellow-600">Medium Risk</TabsTrigger>
            <TabsTrigger value="Low Risk" className="data-[state=active]:text-green-600 data-[state=active]:border-green-600">Low Risk</TabsTrigger>
          </TabsList>
        </Tabs>      
     
       <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-gray-400 hover:text-gray-600"
                onClick={clearSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
           <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-gray-400" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48">
                <SelectValue>
                  {getSortLabel(sortBy)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_date_desc">Newest First</SelectItem>
                <SelectItem value="created_date_asc">Oldest First</SelectItem>
                <SelectItem value="name_asc">Name (A-Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z-A)</SelectItem>
                <SelectItem value="safety_index_desc">Safety Index (High-Low)</SelectItem>
                <SelectItem value="safety_index_asc">Safety Index (Low-High)</SelectItem>
              </SelectContent>
            </Select>
          </div>
            
        <div className="flex items-center">
          <Link 
          to={"/stakingRisk/create-product"} className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline">
            Add new product
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
     </div>
      <ProductList products={products} isLoading={isLoading} /> 
      <NewProductModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        isCreating={isCreating}
        mode="product"
      />
    </div>
  );
}